import React, { useState, useEffect } from 'react';
import './Analytics.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Helper function to validate and parse API responses
  const fetchWithValidation = async (url, options) => {
    try {
      const response = await fetch(url, options);
      
      // First check if response is HTML (error page)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        const text = await response.text();
        if (text.startsWith('<!DOCTYPE')) {
          throw new Error('Server returned HTML instead of JSON. Check API endpoint.');
        }
      }
      
      // Then try to parse as JSON
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (err) {
      console.error('Fetch error:', err);
      throw err;
    }
  };

  // Helper functions
  const countOrderStatuses = (orders) => {
    const statusCount = {
      confirmed: 0,
      out_for_delivery: 0,
      completed: 0,
      cancelled: 0,
      pending: 0
    };
    
    orders?.forEach(order => {
      const status = order?.orderInfo?.orderStatus || 'pending';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    
    return statusCount;
  };

  const countTrialOrderStatuses = (trialOrders) => {
    const statusCount = {
      confirmed: 0,
      completed: 0,
      cancelled: 0,
      failed: 0
    };
    
    trialOrders?.forEach(order => {
      const status = order?.trialStatus || 'confirmed';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
    
    return statusCount;
  };

  const calculateRevenue = (orders) => {
    return orders?.reduce((total, order) => {
      return total + (order?.orderInfo?.totalOrderAmount || 0);
    }, 0) || 0;
  };

  // Fetch all analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        const token = localStorage.getItem('admintoken');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch all data in parallel
        const [usersData, productsData, ordersData, trialOrdersData] = await Promise.all([
          fetchWithValidation('https://admin.riymay.com/api/v1/admin/all/users/info?page=1', { headers }),
          fetchWithValidation('https://admin.riymay.com/api/v1/admin/products?page=1', { headers }),
          fetchWithValidation('https://admin.riymay.com/api/v1/admin/order/management/orders?page=1', { headers }),
          fetchWithValidation('https://admin.riymay.com/api/v1/admin/trail/order/management?page=1', { headers })
        ]);

        setDashboardData({
          users: {
            total: usersData?.totalUsers || 0,
            recent: usersData?.users?.slice(0, 5) || [],
            growth: 12.5
          },
          products: {
            total: productsData?.totalProducts || 0,
            categories: productsData?.categories || [],
            exclusive: productsData?.products?.filter(p => p?.exclusive)?.length || 0
          },
          orders: {
            total: ordersData?.totalOrders || 0,
            recent: ordersData?.data?.slice(0, 5) || [],
            statusCount: countOrderStatuses(ordersData?.data || []),
            revenue: calculateRevenue(ordersData?.data || [])
          },
          trialOrders: {
            total: trialOrdersData?.totalOrders || 0,
            recent: trialOrdersData?.data?.slice(0, 5) || [],
            statusCount: countTrialOrderStatuses(trialOrdersData?.data || [])
          }
        });
        
      } catch (err) {
        console.error('API Error:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  // Data for charts with null checks
  const orderStatusData = dashboardData?.orders?.statusCount ? [
    { name: 'Confirmed', value: dashboardData.orders.statusCount.confirmed || 0 },
    { name: 'Out for Delivery', value: dashboardData.orders.statusCount.out_for_delivery || 0 },
    { name: 'Completed', value: dashboardData.orders.statusCount.completed || 0 },
    { name: 'Cancelled', value: dashboardData.orders.statusCount.cancelled || 0 },
    { name: 'Pending', value: dashboardData.orders.statusCount.pending || 0 }
  ] : [];

  const trialOrderStatusData = dashboardData?.trialOrders?.statusCount ? [
    { name: 'Confirmed', value: dashboardData.trialOrders.statusCount.confirmed || 0 },
    { name: 'Completed', value: dashboardData.trialOrders.statusCount.completed || 0 },
    { name: 'Cancelled', value: dashboardData.trialOrders.statusCount.cancelled || 0 },
    { name: 'Failed', value: dashboardData.trialOrders.statusCount.failed || 0 }
  ] : [];

  const userGrowthData = [
    { name: 'Jan', users: 100 },
    { name: 'Feb', users: 200 },
    { name: 'Mar', users: 350 },
    { name: 'Apr', users: 500 },
    { name: 'May', users: 750 },
    { name: 'Jun', users: dashboardData?.users?.total || 0 }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error Loading Dashboard</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="error-container">
        <h3>No Data Available</h3>
        <p>The dashboard data could not be loaded.</p>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <h1 className="dashboard-title">Admin Dashboard Overview</h1>
      
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Users</h3>
          <p className="card-value">{dashboardData?.users?.total || 0}</p>
          <p className="card-growth">↑ {dashboardData?.users?.growth || 0}% from last month</p>
        </div>
        
        <div className="summary-card">
          <h3>Total Products</h3>
          <p className="card-value">{dashboardData?.products?.total || 0}</p>
          <p className="card-subtext">{dashboardData?.products?.exclusive || 0} exclusive products</p>
        </div>
        
        <div className="summary-card">
          <h3>Total Orders</h3>
          <p className="card-value">{dashboardData?.orders?.total || 0}</p>
          <p className="card-subtext">₹{(dashboardData?.orders?.revenue || 0).toLocaleString()} total revenue</p>
        </div>
        
        <div className="summary-card">
          <h3>Trial Orders</h3>
          <p className="card-value">{dashboardData?.trialOrders?.total || 0}</p>
          <p className="card-subtext">{(dashboardData?.trialOrders?.statusCount?.completed || 0)} completed</p>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3>User Growth</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="users" fill="#8884d8" name="Total Users" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {orderStatusData.length > 0 && (
          <div className="chart-container">
            <h3>Order Status Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      
      {/* Recent Users Table */}
      {dashboardData?.users?.recent?.length > 0 && (
        <div className="data-table-container">
          <h3>Recent Users</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.users.recent.map(user => (
                <tr key={user._id}>
                  <td>{user.firstName} {user.lastName}</td>
                  <td>{user.email}</td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Recent Orders Table */}
      {dashboardData?.orders?.recent?.length > 0 && (
        <div className="data-table-container">
          <h3>Recent Orders</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>User</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.orders.recent.map(order => (
                <tr key={order.orderInfo._id}>
                  <td>{order.orderInfo._id.substring(order.orderInfo._id.length - 8)}</td>
                  <td>{order.orderInfo.userId?.email || 'N/A'}</td>
                  <td>₹{(order.orderInfo.totalOrderAmount || 0).toLocaleString()}</td>
                  <td>
                    <span className={`status-badge ${order.orderInfo.orderStatus?.replace('_', '-')}`}>
                      {order.orderInfo.orderStatus?.replace('_', ' ') || 'pending'}
                    </span>
                  </td>
                  <td>{new Date(order.orderInfo.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Trial Orders Status */}
      <div className="charts-section">
        {trialOrderStatusData.length > 0 && (
          <div className="chart-container">
            <h3>Trial Orders Status</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={trialOrderStatusData}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#82ca9d" name="Trial Orders" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        
        {dashboardData?.products?.categories?.length > 0 && (
          <div className="chart-container">
            <h3>Product Categories</h3>
            <div className="categories-list">
              {dashboardData.products.categories.map(category => (
                <div key={category._id} className="category-item">
                  <h4>{category.name}</h4>
                  <p>{category.subCategories?.length || 0} subcategories</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;